import React, { useEffect, useState } from 'react';
import {
  checkRestoreContinuity,
  fetchMissingChapterContextWithMeta,
  fetchNeighborChapterProse,
  previewRecoveryCandidate,
  restoreRecoveryCandidate,
  restoreRecoveredChapter,
  searchSavedCopies,
} from '../src/utils/recoveryTransactions';
import { ReconstructPanel } from './ReconstructPanel';
import type { ContinuityCheckResult, MissingChapterContext } from '../src/types/missingChapterContext';
import type { RecoveryCandidate, RecoveryCandidatePreview } from '../src/types/recoveryCandidate';

interface MissingChapterModalProps {
  isOpen: boolean;
  projectId: string;
  missingNumbers: number[];
  onClose: () => void;
  onRecovered?: () => void;
}

type TabId = 'CONTEXT' | 'RESTORE' | 'SEARCH' | 'RECONSTRUCT';

function formatCandidateSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatSavedAt(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function BulletList({ items, className = 'text-sm text-cv-secondary' }: { items: string[]; className?: string }) {
  if (!items.length) return null;
  return (
    <ul className={`list-disc list-outside ml-4 space-y-1 leading-relaxed ${className}`}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function ExpandableSection({
  id,
  title,
  count,
  expanded,
  onToggle,
  children,
  tone = 'neutral',
}: {
  id: string;
  title: string;
  count?: number;
  expanded: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
  tone?: 'neutral' | 'amber' | 'red';
}) {
  const border =
    tone === 'amber' ? 'border-amber-900/40' : tone === 'red' ? 'border-red-900/30' : 'border-cv-border-strong';
  return (
    <div className={`border rounded-md ${border} bg-cv-elevated/30`}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium text-cv-primary hover:bg-cv-elevated/60 rounded-md"
        aria-expanded={expanded}
      >
        <span>{title}{count != null && count > 0 ? ` (${count})` : ''}</span>
        <span className="text-cv-muted text-xs shrink-0">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && <div className="px-3 pb-3 pt-0 border-t border-cv-border/80">{children}</div>}
    </div>
  );
}

function AtAGlance({ context }: { context: MissingChapterContext }) {
  if (!context.atAGlance.length) return null;
  return (
    <section className="rounded-md border border-green-900/40 bg-green-950/15 p-3">
      <h3 className="text-sm font-semibold text-green-300 mb-2">
        Chapter {context.missingChapterNumber} at a Glance
      </h3>
      <p className="text-xs text-cv-muted mb-2">Chapter {context.missingChapterNumber} needs to:</p>
      <BulletList items={context.atAGlance} className="text-sm text-cv-primary" />
    </section>
  );
}

function NeighborCard({
  label,
  chapter,
  stateLabel,
  onRead,
  reading,
  readLabel,
  accent,
}: {
  label: string;
  chapter: NonNullable<MissingChapterContext['previousChapter']>;
  stateLabel?: string;
  onRead: () => void;
  reading: boolean;
  readLabel: string;
  accent: 'green' | 'blue';
}) {
  const linkClass = accent === 'green' ? 'text-green-400 hover:text-green-300' : 'text-cv-gold hover:text-cv-interactive';
  return (
    <section className="space-y-1.5">
      <h4 className="text-sm font-medium text-cv-secondary">{label}</h4>
      <div className="rounded-md border border-cv-border-strong bg-cv-elevated/40 p-3 space-y-2 text-sm max-w-prose">
        <p className="font-medium text-cv-primary">{chapter.title}</p>
        {chapter.summary && <p className="text-cv-muted leading-relaxed">{chapter.summary}</p>}
        {stateLabel && chapter.endingState && (
          <p className="text-cv-secondary"><span className="text-cv-muted">{stateLabel}: </span>{chapter.endingState}</p>
        )}
        {stateLabel && chapter.openingState && (
          <p className="text-cv-secondary"><span className="text-cv-muted">{stateLabel}: </span>{chapter.openingState}</p>
        )}
        {chapter.activeCharacters?.length ? (
          <p className="text-xs text-cv-muted">Characters: {chapter.activeCharacters.join(', ')}</p>
        ) : null}
        <button type="button" disabled={reading} onClick={onRead} className={`text-xs font-semibold ${linkClass}`}>
          {reading ? 'Loading…' : readLabel}
        </button>
      </div>
    </section>
  );
}

function ContextPanel({
  context,
  compact,
  expandedSections,
  onToggleSection,
  onReadNeighbor,
  readingChapter,
}: {
  context: MissingChapterContext;
  compact?: boolean;
  expandedSections: Record<string, boolean>;
  onToggleSection: (id: string) => void;
  onReadNeighbor: (n: number) => void;
  readingChapter: number | null;
}) {
  const n = context.missingChapterNumber;
  const atAGlance = context.atAGlance ?? [];
  const confirmedRequirements = context.confirmedRequirements ?? [];
  const likelyRequirements = context.likelyRequirements ?? [];
  const requiredTransitions = context.requiredTransitions ?? [];
  const uncertainties = context.uncertainties ?? [];
  const forbiddenContradictions = context.forbiddenContradictions ?? [];
  const laterConsequences = context.laterConsequences ?? [];
  const characterStates = context.characterStates ?? [];
  const detailedCount =
    confirmedRequirements.length + likelyRequirements.length + requiredTransitions.length;

  return (
    <div className={`space-y-4 ${compact ? 'text-sm' : ''}`}>
      {!compact && atAGlance.length > 0 && <AtAGlance context={{ ...context, atAGlance }} />}

      {context.previousChapter && (
        <NeighborCard
          label="What came before"
          chapter={context.previousChapter}
          stateLabel={
            context.previousChapter.endingState &&
            context.previousChapter.endingState !== context.previousChapter.summary
              ? 'Known ending context'
              : undefined
          }
          onRead={() => onReadNeighbor(context.previousChapter!.chapterNumber)}
          reading={readingChapter === context.previousChapter.chapterNumber}
          readLabel="Read Previous Chapter"
          accent="green"
        />
      )}

      {context.nextChapter && (
        <NeighborCard
          label="What comes next"
          chapter={context.nextChapter}
          stateLabel={
            context.nextChapter.openingState &&
            context.nextChapter.openingState !== context.nextChapter.summary
              ? 'Opening context'
              : undefined
          }
          onRead={() => onReadNeighbor(context.nextChapter!.chapterNumber)}
          reading={readingChapter === context.nextChapter.chapterNumber}
          readLabel="Read Following Chapter"
          accent="blue"
        />
      )}

      {context.roadmapEntry && (
        <section className="space-y-1.5">
          <h4 className="text-sm font-medium text-cv-secondary">Original plan</h4>
          <div className="rounded-md border border-cv-border-strong bg-cv-elevated/40 p-3 space-y-1.5 text-sm max-w-prose">
            <p className="font-medium text-cv-primary">{context.roadmapEntry.title}</p>
            {context.roadmapEntry.summary && (
              <p className="text-cv-muted leading-relaxed">{context.roadmapEntry.summary}</p>
            )}
            {context.roadmapEntry.pov && (
              <p className="text-xs text-cv-muted">POV: {context.roadmapEntry.pov}</p>
            )}
            {context.roadmapEntry.location && (
              <p className="text-xs text-cv-muted">Location: {context.roadmapEntry.location}</p>
            )}
          </div>
        </section>
      )}

      {!compact && (
        <>
          <ExpandableSection
            id="detailed"
            title="Detailed requirements"
            count={detailedCount}
            expanded={!!expandedSections.detailed}
            onToggle={onToggleSection}
          >
            <div className="space-y-3 pt-2">
              {confirmedRequirements.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-green-400/90 mb-1">Confirmed</p>
                  <BulletList items={confirmedRequirements} />
                </div>
              )}
              {likelyRequirements.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-cv-gold/90 mb-1">Strongly implied</p>
                  <BulletList items={likelyRequirements} />
                </div>
              )}
              {requiredTransitions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-cv-muted mb-1">Required transition</p>
                  <BulletList items={requiredTransitions} />
                </div>
              )}
            </div>
          </ExpandableSection>

          {laterConsequences.length > 0 && (
            <ExpandableSection
              id="later"
              title="Later chapter evidence"
              count={laterConsequences.length}
              expanded={!!expandedSections.later}
              onToggle={onToggleSection}
            >
              <ul className="space-y-2 pt-2 text-sm text-cv-secondary leading-relaxed max-w-prose">
                {laterConsequences.map((c, i) => (
                  <li key={i}>{c.evidenceNote}</li>
                ))}
              </ul>
            </ExpandableSection>
          )}

          {characterStates.length > 0 && (
            <ExpandableSection
              id="characters"
              title="Character and relationship states"
              count={characterStates.length}
              expanded={!!expandedSections.characters}
              onToggle={onToggleSection}
            >
              <ul className="pt-2 space-y-1 text-sm text-cv-secondary">
                {characterStates.map((c, i) => (
                  <li key={i}><span className="font-medium">{c.name}</span>{c.stateBefore ? ` — ${c.stateBefore}` : ''}</li>
                ))}
              </ul>
            </ExpandableSection>
          )}

          {(forbiddenContradictions.length > 0 || uncertainties.length > 0) && (
            <ExpandableSection
              id="restrictions"
              title="Continuity restrictions and uncertainties"
              count={forbiddenContradictions.length + uncertainties.length}
              expanded={!!expandedSections.restrictions}
              onToggle={onToggleSection}
              tone="amber"
            >
              <div className="space-y-3 pt-2">
                {forbiddenContradictions.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-cv-danger/80 mb-1">Must not contradict</p>
                    <BulletList items={forbiddenContradictions} className="text-sm text-cv-secondary" />
                  </div>
                )}
                {uncertainties.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-cv-warning/80 mb-1">Uncertain</p>
                    <BulletList items={uncertainties} className="text-sm text-cv-secondary" />
                  </div>
                )}
              </div>
            </ExpandableSection>
          )}
        </>
      )}

      {compact && atAGlance.length > 0 && (
        <div className="rounded border border-cv-border p-2">
          <p className="text-xs font-medium text-cv-muted mb-1">Chapter {n} at a Glance</p>
          <BulletList items={atAGlance} className="text-xs text-cv-secondary" />
        </div>
      )}
    </div>
  );
}

export function MissingChapterModal({ isOpen, projectId, missingNumbers, onClose, onRecovered }: MissingChapterModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('CONTEXT');
  const [context, setContext] = useState<MissingChapterContext | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState('');
  const [contextWarning, setContextWarning] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [chapterTitle, setChapterTitle] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState(false);
  const [candidates, setCandidates] = useState<RecoveryCandidate[]>([]);
  const [searchHasRun, setSearchHasRun] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [candidatePreview, setCandidatePreview] = useState<RecoveryCandidatePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [restoreCandidateTarget, setRestoreCandidateTarget] = useState<RecoveryCandidate | null>(null);
  const [confirmCandidateRestore, setConfirmCandidateRestore] = useState(false);
  const [isRestoringCandidate, setIsRestoringCandidate] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [continuityResult, setContinuityResult] = useState<ContinuityCheckResult | null>(null);
  const [isCheckingContinuity, setIsCheckingContinuity] = useState(false);
  const [neighborReader, setNeighborReader] = useState<{ title: string; content: string } | null>(null);
  const [readingChapter, setReadingChapter] = useState<number | null>(null);

  const targetChapter = missingNumbers[0];

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (!isOpen || !projectId || !targetChapter) return;
    setActiveTab('CONTEXT');
    setContext(null);
    setContextError('');
    setContextWarning('');
    setRestoreError('');
    setRestoreSuccess(false);
    setContinuityResult(null);
    setNeighborReader(null);
    setExpandedSections({});
    setChapterTitle('');
    setPastedText('');
    setConfirmRestore(false);
    setCandidates([]);
    setSearchHasRun(false);
    setSearchError('');
    setCandidatePreview(null);
    setRestoreCandidateTarget(null);
    setConfirmCandidateRestore(false);

    let cancelled = false;
    (async () => {
      setContextLoading(true);
      try {
        const { context: normalized, hadMissingFields } = await fetchMissingChapterContextWithMeta(
          projectId,
          targetChapter,
        );
        if (!cancelled) {
          setContext(normalized);
          setChapterTitle(normalized.roadmapEntry?.title || `Chapter ${targetChapter}`);
          if (hadMissingFields) {
            setContextWarning(
              'Some recovery context fields were unavailable from the server. Partial guidance is shown below.',
            );
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setContextError(e instanceof Error ? e.message : 'Failed to load recovery context.');
        }
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, projectId, targetChapter]);

  const handleSearch = async () => {
    setIsSearching(true);
    setSearchError('');
    setRestoreError('');
    setCandidatePreview(null);
    setRestoreCandidateTarget(null);
    setConfirmCandidateRestore(false);
    try {
      const results = await searchSavedCopies(projectId, targetChapter);
      setCandidates(results || []);
      setSearchHasRun(true);
    } catch (e: unknown) {
      setSearchError('Saved copies could not be checked. Try again.');
      setCandidates([]);
      setSearchHasRun(true);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (!isOpen || activeTab !== 'SEARCH' || !projectId || searchHasRun) return;
    void handleSearch();
  }, [isOpen, activeTab, projectId, targetChapter, searchHasRun]);

  if (!isOpen || missingNumbers.length === 0) return null;

  const handlePreviewCandidate = async (candidate: RecoveryCandidate) => {
    setPreviewLoading(true);
    setRestoreError('');
    setRestoreCandidateTarget(null);
    setConfirmCandidateRestore(false);
    try {
      const preview = await previewRecoveryCandidate(projectId, targetChapter, candidate.candidateId);
      setCandidatePreview(preview);
    } catch (e: unknown) {
      setRestoreError(e instanceof Error ? e.message : 'This saved file could not be verified as Chapter ' + targetChapter + '.');
      setCandidatePreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRestoreCandidate = async () => {
    if (!restoreCandidateTarget) return;
    if (!confirmCandidateRestore) {
      setRestoreError('Check the confirmation box to restore this saved copy.');
      return;
    }
    const opId = crypto.randomUUID();
    setIsRestoringCandidate(true);
    setRestoreError('');
    try {
      await restoreRecoveryCandidate(projectId, targetChapter, opId, restoreCandidateTarget.candidateId);
      setRestoreSuccess(true);
      if (onRecovered) onRecovered();
      setTimeout(() => onClose(), 1500);
    } catch (e: unknown) {
      setRestoreError(e instanceof Error ? e.message : 'Failed to restore saved copy.');
    } finally {
      setIsRestoringCandidate(false);
    }
  };

  const handleReadNeighbor = async (chapterNumber: number) => {
    setReadingChapter(chapterNumber);
    try {
      const prose = await fetchNeighborChapterProse(projectId, targetChapter, chapterNumber);
      setNeighborReader({ title: prose.title, content: prose.content });
    } catch (e: unknown) {
      setRestoreError(e instanceof Error ? e.message : 'Failed to load chapter.');
    } finally {
      setReadingChapter(null);
    }
  };

  const handleCheckContinuity = async () => {
    if (!chapterTitle.trim() || !pastedText.trim()) {
      setRestoreError('Enter a chapter title and text before checking continuity.');
      return;
    }
    setIsCheckingContinuity(true);
    setRestoreError('');
    setContinuityResult(null);
    try {
      const result = await checkRestoreContinuity(projectId, targetChapter, chapterTitle, pastedText);
      setContinuityResult(result);
    } catch (e: unknown) {
      setRestoreError(e instanceof Error ? e.message : 'Continuity check failed.');
    } finally {
      setIsCheckingContinuity(false);
    }
  };

  const handlePasteRestore = async () => {
    console.log('[RECOVERY UI] Restore clicked', { projectId, chapterNumber: targetChapter });
    if (!projectId?.trim()) {
      setRestoreError('Project is not loaded. Close this modal and reopen the project.');
      return;
    }
    const title = chapterTitle.trim();
    if (!title) { setRestoreError('Enter a chapter title before restoring.'); return; }
    if (!pastedText.trim()) { setRestoreError('Enter chapter text before restoring.'); return; }
    if (!confirmRestore) { setRestoreError('Check the confirmation box to restore this chapter.'); return; }

    const opId = crypto.randomUUID();
    setIsRestoring(true);
    setRestoreError('');
    try {
      await restoreRecoveredChapter(projectId, targetChapter, opId, 'ORIGINAL_RESTORED', title, pastedText, 'User restored original chapter text');
      setRestoreSuccess(true);
      if (onRecovered) onRecovered();
      setTimeout(() => onClose(), 1500);
    } catch (e: unknown) {
      setRestoreError(e instanceof Error ? e.message : 'Failed to restore chapter');
    } finally {
      setIsRestoring(false);
    }
  };

  const tabs: { id: TabId; label: string; disabled?: boolean }[] = [
    { id: 'CONTEXT', label: 'Context' },
    { id: 'RESTORE', label: 'Restore Original' },
    { id: 'SEARCH', label: 'Search Copies' },
    { id: 'RECONSTRUCT', label: 'Reconstruct' },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4">
      <div
        className="bg-cv-panel border border-cv-border-strong rounded-lg max-w-3xl w-full shadow-2xl flex flex-col max-h-[min(90vh,900px)] min-h-0"
        role="dialog"
        aria-labelledby="missing-chapter-title"
      >
        {/* Fixed header */}
        <div className="shrink-0 px-4 py-3 border-b border-cv-border bg-cv-panel flex items-center justify-between gap-3">
          <h2 id="missing-chapter-title" className="text-base sm:text-lg font-bold text-cv-primary flex items-center gap-2 min-w-0">
            <svg className="w-5 h-5 text-cv-danger shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="truncate">Chapter {targetChapter} Is Missing</span>
          </h2>
          <button type="button" onClick={onClose} className="shrink-0 p-1 text-cv-muted hover:text-cv-primary" disabled={isRestoring} aria-label="Close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sticky tab row */}
        <div className="shrink-0 flex border-b border-cv-border bg-cv-panel overflow-x-auto scrollbar-thin" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              disabled={tab.disabled || isRestoring}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              className={`shrink-0 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-cv-primary border-cv-success bg-cv-elevated/40'
                  : tab.disabled
                    ? 'text-cv-disabled border-transparent cursor-not-allowed'
                    : 'text-cv-muted border-transparent hover:text-cv-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-5 custom-scrollbar">
          {restoreSuccess ? (
            <div className="py-8 text-center space-y-2">
              <h3 className="text-lg font-semibold text-cv-primary">Chapter Restored Successfully</h3>
              <p className="text-cv-muted text-sm">The manuscript integrity has been repaired.</p>
            </div>
          ) : (
            <>
              {activeTab === 'CONTEXT' && (
                <div className="space-y-4">
                  {contextLoading && <p className="text-cv-muted text-sm">Compiling recovery context…</p>}
                  {contextWarning && (
                    <div className="p-3 bg-amber-950/40 border border-amber-900/50 rounded text-cv-warning text-sm">
                      {contextWarning}
                    </div>
                  )}
                  {contextError && (
                    <div className="p-3 bg-red-950/50 border border-red-900/50 rounded text-cv-danger text-sm">{contextError}</div>
                  )}
                  {context && (
                    <ContextPanel
                      context={context}
                      expandedSections={expandedSections}
                      onToggleSection={toggleSection}
                      onReadNeighbor={handleReadNeighbor}
                      readingChapter={readingChapter}
                    />
                  )}
                </div>
              )}

              {activeTab === 'RESTORE' && (
                <div className="space-y-4">
                  {context && (
                    <ContextPanel
                      context={context}
                      compact
                      expandedSections={expandedSections}
                      onToggleSection={toggleSection}
                      onReadNeighbor={handleReadNeighbor}
                      readingChapter={readingChapter}
                    />
                  )}
                  <div>
                    <label htmlFor="recovery-chapter-title" className="block text-xs font-medium text-cv-muted mb-1">
                      Chapter Title
                    </label>
                    <input
                      id="recovery-chapter-title"
                      type="text"
                      value={chapterTitle}
                      onChange={(e) => setChapterTitle(e.target.value)}
                      className="w-full bg-cv-page border border-cv-border-strong rounded px-3 py-2 text-sm text-cv-primary"
                      disabled={isRestoring}
                    />
                  </div>
                  <textarea
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder={`Paste the original content for Chapter ${targetChapter} here…`}
                    className="w-full h-40 sm:h-48 bg-cv-page border border-cv-border-strong rounded p-3 text-sm text-cv-secondary font-serif resize-y min-h-[8rem]"
                    disabled={isRestoring}
                  />
                  <label className="flex items-start gap-2 text-sm text-cv-secondary">
                    <input type="checkbox" checked={confirmRestore} onChange={(e) => setConfirmRestore(e.target.checked)} disabled={isRestoring} className="mt-0.5" />
                    <span>I confirm this is the original text for Chapter {targetChapter} and want to restore it.</span>
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button type="button" onClick={handleCheckContinuity} disabled={isCheckingContinuity || isRestoring || !pastedText.trim()} className="flex-1 py-2 border border-cv-border-strong text-cv-primary rounded text-sm font-medium disabled:opacity-50">
                      {isCheckingContinuity ? 'Checking…' : 'Check Continuity'}
                    </button>
                    <button type="button" onClick={handlePasteRestore} disabled={isRestoring || !pastedText.trim() || !chapterTitle.trim() || !confirmRestore} className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-cv-primary rounded text-sm font-semibold disabled:opacity-50">
                      {isRestoring ? 'Restoring…' : 'Restore to Manuscript'}
                    </button>
                  </div>
                  {continuityResult && (
                    <div className={`p-3 rounded text-sm border ${continuityResult.passed && continuityResult.warnings.length === 0 ? 'bg-green-950/30 border-green-800/60 text-green-200' : 'bg-amber-950/30 border-cv-warning/30 text-cv-warning'}`}>
                      <p className="font-medium mb-1">{continuityResult.message}</p>
                      {continuityResult.warnings.map((w, i) => <p key={i} className="text-xs mt-1 opacity-90">• {w}</p>)}
                    </div>
                  )}
                  {restoreError && <div className="p-3 bg-red-950/50 border border-red-900/50 rounded text-cv-danger text-sm">{restoreError}</div>}
                </div>
              )}

              {activeTab === 'SEARCH' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-cv-muted">Search cloud storage for a saved copy of Chapter {targetChapter}.</p>
                    <button type="button" onClick={handleSearch} className="text-xs text-cv-gold font-medium shrink-0" disabled={isSearching || isRestoringCandidate}>
                      {isSearching ? 'Scanning…' : 'Scan again'}
                    </button>
                  </div>

                  {isSearching && <p className="text-cv-muted text-sm">Scanning cloud storage…</p>}

                  {searchError && (
                    <div className="p-3 bg-red-950/50 border border-red-900/50 rounded text-cv-danger text-sm">{searchError}</div>
                  )}

                  {!isSearching && searchHasRun && candidates.length === 0 && !searchError && (
                    <div className="p-4 rounded border border-cv-border-strong bg-cv-elevated/40 text-sm text-cv-secondary">
                      <p>No saved copies of Chapter {targetChapter} were found.</p>
                      <p className="text-cv-muted text-xs mt-2">You can paste the original text or reconstruct a replacement.</p>
                    </div>
                  )}

                  {candidates.length > 0 && (
                    <div className="space-y-3">
                      {candidates.map((c) => (
                        <div key={c.candidateId} className="p-3 bg-cv-elevated/50 rounded border border-cv-border-strong space-y-2">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-cv-primary text-sm">{c.title || `Recovered Chapter ${targetChapter}`}</p>
                              <p className="text-xs text-cv-muted mt-0.5">{c.displaySource}</p>
                              <p className="text-xs text-cv-muted mt-1">
                                {formatCandidateSize(c.contentLength)}
                                {formatSavedAt(c.savedAt) ? ` · Saved ${formatSavedAt(c.savedAt)}` : ''}
                              </p>
                              {c.highConfidence && (
                                <p className="text-xs text-green-400/90 mt-1 font-medium">High-confidence saved copy</p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={previewLoading || isRestoringCandidate}
                              onClick={() => handlePreviewCandidate(c)}
                              className="px-3 py-1.5 text-xs font-medium border border-cv-border-strong text-cv-primary rounded hover:bg-cv-elevated/50 disabled:opacity-50"
                            >
                              {previewLoading ? 'Loading…' : 'Preview Copy'}
                            </button>
                            <button
                              type="button"
                              disabled={isRestoringCandidate}
                              onClick={() => {
                                setRestoreCandidateTarget(c);
                                setCandidatePreview(null);
                                setConfirmCandidateRestore(false);
                              }}
                              className="px-3 py-1.5 text-xs font-semibold bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded disabled:opacity-50"
                            >
                              Restore This Copy
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {candidatePreview && (
                    <div className="rounded border border-cv-interactive/30 bg-cv-interactive-soft p-4 space-y-3 text-sm">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="font-semibold text-cv-primary">{candidatePreview.title}</p>
                          <p className="text-xs text-cv-muted">Chapter {candidatePreview.chapterNumber} · {candidatePreview.displaySource}</p>
                          <p className="text-xs text-cv-muted">{formatCandidateSize(candidatePreview.contentLength)}</p>
                        </div>
                        <button type="button" onClick={() => setCandidatePreview(null)} className="text-xs text-cv-muted hover:text-cv-primary">Close</button>
                      </div>
                      {candidatePreview.titleMismatchWarning && (
                        <p className="text-xs text-cv-warning bg-amber-950/30 border border-amber-900/40 rounded p-2">{candidatePreview.titleMismatchWarning}</p>
                      )}
                      <div>
                        <p className="text-xs font-medium text-cv-muted mb-1">Opening excerpt</p>
                        <p className="text-cv-secondary font-serif leading-relaxed text-xs whitespace-pre-wrap">{candidatePreview.openingExcerpt}</p>
                      </div>
                      {candidatePreview.closingExcerpt && (
                        <div>
                          <p className="text-xs font-medium text-cv-muted mb-1">Closing excerpt</p>
                          <p className="text-cv-secondary font-serif leading-relaxed text-xs whitespace-pre-wrap">{candidatePreview.closingExcerpt}</p>
                        </div>
                      )}
                      {context?.atAGlance && context.atAGlance.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-cv-muted mb-1">Chapter {targetChapter} at a Glance</p>
                          <BulletList items={context.atAGlance} className="text-xs text-cv-muted" />
                        </div>
                      )}
                    </div>
                  )}

                  {restoreCandidateTarget && (
                    <div className="rounded border border-green-900/40 bg-green-950/15 p-4 space-y-3 text-sm">
                      <p className="font-semibold text-green-200">Restore this saved copy as Chapter {targetChapter}?</p>
                      <ul className="text-cv-secondary text-xs space-y-1 list-disc list-inside">
                        <li>Title: {restoreCandidateTarget.title || `Chapter ${targetChapter}`}</li>
                        <li>Chapter: {targetChapter}</li>
                        <li>Source: {restoreCandidateTarget.displaySource}</li>
                        <li>Size: {formatCandidateSize(restoreCandidateTarget.contentLength)}</li>
                      </ul>
                      <p className="text-xs text-cv-warning/90">No existing chapter will be overwritten. Chapter {targetChapter} is currently missing.</p>
                      <label className="flex items-start gap-2 text-sm text-cv-secondary">
                        <input
                          type="checkbox"
                          checked={confirmCandidateRestore}
                          onChange={(e) => setConfirmCandidateRestore(e.target.checked)}
                          disabled={isRestoringCandidate}
                          className="mt-0.5"
                        />
                        <span>I reviewed this copy and want to restore it to the manuscript.</span>
                      </label>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setRestoreCandidateTarget(null)} className="flex-1 py-2 border border-cv-border-strong rounded text-xs" disabled={isRestoringCandidate}>Cancel</button>
                        <button
                          type="button"
                          onClick={handleRestoreCandidate}
                          disabled={isRestoringCandidate || !confirmCandidateRestore}
                          className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-cv-primary rounded text-xs font-semibold disabled:opacity-50"
                        >
                          {isRestoringCandidate ? 'Restoring…' : 'Restore Saved Copy'}
                        </button>
                      </div>
                    </div>
                  )}

                  {restoreError && activeTab === 'SEARCH' && (
                    <div className="p-3 bg-red-950/50 border border-red-900/50 rounded text-cv-danger text-sm">{restoreError}</div>
                  )}
                </div>
              )}

              {activeTab === 'RECONSTRUCT' && (
                <ReconstructPanel
                  projectId={projectId}
                  targetChapter={targetChapter}
                  context={context}
                  onRecovered={() => {
                    setRestoreSuccess(true);
                    onRecovered?.();
                    setTimeout(() => onClose(), 1500);
                  }}
                  onError={(msg) => setRestoreError(msg)}
                  disabled={!context || contextLoading}
                />
              )}
            </>
          )}
        </div>

        {neighborReader && (
          <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" onClick={() => setNeighborReader(null)}>
            <div className="bg-cv-panel border border-cv-border-strong rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col min-h-0" onClick={(e) => e.stopPropagation()}>
              <div className="shrink-0 p-3 border-b border-cv-border-strong flex justify-between items-center">
                <h3 className="font-semibold text-cv-primary text-sm">{neighborReader.title}</h3>
                <button type="button" onClick={() => setNeighborReader(null)} className="text-cv-muted hover:text-cv-primary text-sm">Close</button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4 text-sm text-cv-secondary font-serif whitespace-pre-wrap leading-relaxed">{neighborReader.content}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
